import React, { useEffect } from 'react'
import styled from 'styled-components'
import confetti from 'canvas-confetti'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import {
    type CheckoutSuccessfulLogicDependencies,
    CheckoutSuccessfulLogic,
} from './logic'
import { LoadingIndicator } from '~common/components/loading' // Adjust path if needed

export interface CheckoutSuccessfulProps
    extends CheckoutSuccessfulLogicDependencies {}

export function CheckoutSuccessful(props: CheckoutSuccessfulProps) {
    const appContext = useAppContext()
    const { state } = useLogic(CheckoutSuccessfulLogic, {
        ...props,
        ...appContext,
    })

    useEffect(() => {
        if (state.loadState === 'success') {
            // Side Cannons confetti (adapted from Magic UI docs)
            const end = Date.now() + 3 * 1000 // 3 seconds duration
            const colors = ['#a786ff', '#6ce045', '#eca184', '#f8deb1']

            const frame = () => {
                if (Date.now() > end) return

                // Left side burst
                confetti({
                    particleCount: 3,
                    angle: 90,
                    spread: 200,
                    startVelocity: 50,
                    origin: { x: 0.5, y: 1.2 },
                    colors,
                    gravity: 0.8,
                    decay: 0.9,
                    scalar: 0.8,
                    ticks: 200,
                })
                // // Right side burst
                // confetti({
                //     particleCount: 3,
                //     angle: 120,
                //     spread: 55,
                //     startVelocity: 60,
                //     origin: { x: 1, y: 0.5 },
                //     colors,
                // })

                requestAnimationFrame(frame)
            }

            frame()
        }
    }, [state.loadState])

    if (state.loadState === 'running') {
        return (
            <Container>
                <LoadingIndicator size="small" />
                <span>Verifying payment...</span>
            </Container>
        )
    }

    if (state.loadState === 'error') {
        return (
            <Container>
                <h1>Verification Failed</h1>
                <p>
                    {state.errorMessage ||
                        'An error occurred while verifying your payment.'}
                </p>
                <p>Please contact support if the issue persists.</p>
            </Container>
        )
    }

    return (
        <Container>
            <Title>
                You have <u>{state.totalInvoiceCredits}</u>
                <br /> invoice credits!
            </Title>
            <Subtitle>You can close this tab now</Subtitle>
        </Container>
    )
}

const Container = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    min-width: 300px;
    padding: 2rem;
    text-align: center;

    h1 {
        color: ${({ theme }) => theme.colors.text.primary};
        margin-bottom: 1rem;
    }

    p {
        color: ${({ theme }) => theme.colors.text.secondary};
        margin-bottom: 0.5rem;
    }
`

const Title = styled.h1`
    font-size: ${({ theme }) => theme.typography.fontSize['5xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 1rem;
`

const Subtitle = styled.p`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    color: ${({ theme }) => theme.colors.text.secondary};
    max-width: 400px;
    line-height: 1.6;
`
